<template>
  <section>
    <b-modal
      id="listContactsWithoutEmailModal"
      ref="listContactsWithoutEmailModal"
      no-close-on-backdrop
      :title="$t('message.tableColumnHeadings.information')"
      hide-footer
      @close="closeModal">
      <h5 v-if="$route.name.includes('MarketingObjectDetailsView')">
        {{ $t('message.onBoarding.interestedParties.modal.requestWithoutEmail') }}
      </h5>
      <h5 v-else>
        {{ $t('message.onBoarding.interestedParties.modal.contactWithoutEmail', {source: getSource()}) }}
      </h5>
      <div class="col pl-0">
        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="mb-2">
          <span class="ml-2 mr-2">-</span>
          <a
            tabindex="0"
            target="_blank"
            @click="redirectToDetailPageMiddle(contact.id)"
            @click.middle="redirectToDetailPageMiddle(contact.id)">
            {{ getContactName(contact.id) }}
          </a>
          <span
            v-if="hasJoinTenant(contact.id).length > 0">
            {{ '(' + (['tenants', 'objects'].includes(source) ? $t('message.onBoarding.interestedParties.listContactsWithoutEmailModal.hasJoinTenant') : $t('message.onBoarding.interestedParties.listContactsWithoutEmailModal.hasJoinCondo')) + ':' }}
            <span
              v-for="( tenantId, index) in hasJoinTenant(contact.id)"
              :key="index">
              <span v-if="index !== 0">
                ,
              </span>
              <a
                tabindex="0"
                target="_blank"
                @click="redirectToDetailPageMiddle(tenantId)"
                @click.middle="redirectToDetailPageMiddle(tenantId)">
                {{ getContactName(tenantId) }}
              </a>
              <span v-if="index === hasJoinTenant(contact.id).length -1">
                )
              </span>
            </span>
          </span>
        </div>
      </div>
    </b-modal>
  </section>
</template>
<script>
import { routeChecks } from '@/mixins/routeChecks'
import ContactApi from '@/misc/apis/ContactApi'
import { hasSendableEmail } from '@/misc/utils/contactEmail'
import Vue from 'vue'
import * as Sentry from '@sentry/vue'

export default {
  name: 'ListContactWithoutEmailModal',
  components: {
  },
  mixins: [routeChecks],
  props: {
    contacts: {
      type: Array,
      default: () => {
        return []
      }
    },
    allContacts: {
      type: [Array, Object],
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      source: 'contacts',
      contactIds: [],
      contactList: [],
      // One capture per missing id — getContactName runs from the template (COOZ-15700)
      reportedMissingContactIds: {}
    }
  },
  computed: {
    listRequest() {
      return this.$store.getters['request/getRequests'] || []
    },
    selectedEntries() {
      return this.$store.getters['message/getSelectedEntries'] || []
    },
    normalizedAllContacts() {
      return this.normalizeContactList(this.allContacts)
    },
    /** Deduped contacts from resolve + props for name lookup and join map (COOZ-15700). */
    resolvedContacts() {
      const byId = new Map()
      const sources = [
        ...this.normalizeContactList(this.contactList),
        ...this.normalizeContactList(this.contacts),
        ...this.normalizedAllContacts
      ]
      sources.forEach(contact => {
        if (contact?.id) {
          byId.set(contact.id, contact)
        }
      })
      return Array.from(byId.values())
    },
    contactsById() {
      const map = new Map()
      this.resolvedContacts.forEach(contact => {
        map.set(contact.id, contact)
      })
      return map
    },
    contactJoinMap() {
      const joinMap = new Map()
      if (this.resolvedContacts.length === 0) {
        return joinMap
      }

      const globalsContacts = this.resolvedContacts

      this.selectedEntries.forEach(element => {
        if (this.source === 'tenants') {
          this.processTenantEntries(element, globalsContacts, joinMap)
        } else if (this.source === 'condoOwners') {
          this.processCondoOwnerEntries(element, globalsContacts, joinMap)
        } else if (this.source === 'objects') {
          this.processObjectEntries(element, globalsContacts, joinMap)
        } else if (this.source === 'condominium') {
          this.processCondominiumEntries(element, globalsContacts, joinMap)
        }
      })

      return joinMap
    }
  },
  watch: {
    allContacts: {
      immediate: true,
      handler(newVal) {
        this.mergeContactsIntoList(this.normalizeContactList(newVal))
      }
    },
    contacts: {
      immediate: true,
      handler(newVal) {
        this.mergeContactsIntoList(this.normalizeContactList(newVal))
      }
    }
  },
  mounted: function () {
    this.getSource()
    this.selectedEntries.forEach(element => {
      if (element.tenancy && element.tenancy.tenants?.length > 0) {
        this.contactIds = this.contactIds.concat(element.tenancy.tenants.map(x => x.contactId))
      }
      if (element.tenants?.length > 0) {
        this.contactIds = this.contactIds.concat(element.tenants.map(x => x.contactId))
      }
      if (element.tenant && element.tenant.contactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.tenant.contactIds)
      }
      if (element.condominium && element.condominium.owner?.length > 0) {
        this.contactIds = this.contactIds.concat(element.condominium.owner.map(x => x.contactId))
      }
      if (element.condominium?.roommates?.length > 0) {
        this.contactIds = this.contactIds.concat(element.condominium.roommates.map(x => x.contactId))
      }
      if (element.condominiumOwners?.length > 0) {
        this.contactIds = this.contactIds.concat(element.condominiumOwners.map(x => x.contactId))
      }
      if (element.roommates?.length > 0) {
        this.contactIds = this.contactIds.concat(element.roommates.map(x => x.contactId))
      }
      if (element.owner && element.owner.contactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.owner.contactIds)
      }
      if (element.owner?.roommateContactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.owner.roommateContactIds)
      }
      if (element.tenants && element.tenants.tenant?.contactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.tenants.tenant?.contactIds)
      }
      if (element.condominium && element.condominium.owner?.contactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.condominium.owner?.contactIds)
      }
      if (element.condominium?.owner?.roommateContactIds?.length > 0) {
        this.contactIds = this.contactIds.concat(element.condominium.owner.roommateContactIds)
      }
    })
    this.contactIds = [...new Set(this.contactIds.filter(Boolean))]
    this.loadContacts()
  },
  methods: {
    normalizeContactList(value) {
      if (!value) {
        return []
      }
      if (Array.isArray(value)) {
        return value.filter(Boolean)
      }
      if (typeof value === 'object') {
        return [value]
      }
      return []
    },
    mergeContactsIntoList(contacts) {
      if (!contacts?.length) {
        return
      }
      const existingIds = new Set(this.contactList.map(c => c?.id).filter(Boolean))
      const toAdd = contacts.filter(c => c?.id && !existingIds.has(c.id))
      if (toAdd.length > 0) {
        this.contactList = this.contactList.concat(toAdd)
      }
    },
    expandCompaniesWithContactPersons(persons, companies) {
      const expanded = [...(persons || []), ...(companies || [])]
      ;(companies || []).forEach(company => {
        ;(company.contactPersons || []).forEach(person => {
          if (person?.id) {
            expanded.push(person)
          }
        })
      })
      return expanded
    },
    formatContactDisplayName(contact) {
      if (!contact) {
        return '-'
      }
      if (contact.name) {
        return contact.name
      }
      const parts = [contact.firstName, contact.lastName].filter(Boolean)
      return parts.length > 0 ? parts.join(' ') : '-'
    },
    reportMissingContactName(contactId) {
      const key = contactId || '__empty_lists__'
      if (this.reportedMissingContactIds[key]) {
        return
      }
      this.$set(this.reportedMissingContactIds, key, true)
      const isJoinTenant = contactId
        ? [...this.contactJoinMap.values()].some(ids => ids.includes(contactId))
        : false
      Sentry.setExtra('contactId', contactId)
      Sentry.setExtra('source', this.source)
      Sentry.setExtra('isJoinTenant', isJoinTenant)
      Sentry.setExtra('contactList.length', this.contactList?.length || 0)
      Sentry.setExtra('contacts.length', this.contacts?.length || 0)
      Sentry.setExtra('allContacts.length', this.normalizedAllContacts.length)
      Sentry.setExtra('resolvedContacts.length', this.resolvedContacts.length)
      Sentry.captureMessage('Contacts names couldn\'t be processed.')
    },
    getContactName(contactId) {
      if (this.resolvedContacts.length === 0) {
        this.reportMissingContactName(contactId)
        return '-'
      }
      const contact = this.contactsById.get(contactId)
      if (!contact) {
        this.reportMissingContactName(contactId)
        return '-'
      }
      return this.formatContactDisplayName(contact)
    },
    contactsWithSendableEmail(globalsContacts, ids) {
      return globalsContacts.filter(contact => contact && ids.includes(contact.id) && hasSendableEmail(contact))
    },
    redirectToDetailPageMiddle(contactId) {
      if (contactId !== null) {
        let route = ''
        if (this.isOwnerModule) {
          route = this.$router.resolve({
            name: 'OwnerContactDetailsView',
            params: { id: contactId }
          })
        } else if (this.isAssetModule) {
          route = this.$router.resolve({
            name: 'AssetContactDetailsView',
            params: { id: contactId }
          })
        } else if (this.isAccountingModule) {
          route = this.$router.resolve({
            name: 'AccountingContactDetailsView',
            params: { id: contactId }
          })
        } else if (this.isMarketingModule) {
          route = this.$router.resolve({
            name: 'MarketingContactDetailsView',
            params: { id: contactId }
          })
        } else {
          route = this.$router.resolve({
            name: 'AdminContactDetailsView',
            params: { id: contactId }
          })
        }
        window.open(route.href)
      }
    },
    closeModal() {
      this.$bvModal.hide('listContactsWithoutEmailModal')
      this.$store.dispatch('request/setRequests', [])
      this.contactsWithoutEmail = []
    },
    getSource() {
      // values possible for source = ['tenants', 'condoOwners', 'contacts', 'requests', 'objects']
      if (this.$route.name.includes('BuildingDetailsView') && this.$route.query.view === 'objects') {
        if (this.selectedEntries.length > 0) {
          if (this.selectedEntries[0].condominiumOwnership === true) {
            this.source = 'condominium'
            return this.$t('message.onBoarding.interestedParties.modal.condoOwners')
          } else {
            this.source = 'objects'
            return this.$t('message.onBoarding.interestedParties.modal.tenants')
          }
        }
      } else if (this.$route.name.includes('TenanciesView') || this.$route.name.includes('RentalObjectOverview') || (this.$route.name.includes('BuildingDetailsView') && this.$route.query.view === 'tenants')) {
        this.source = 'tenants'
        return this.$t('message.onBoarding.interestedParties.modal.tenants')
      } else if (this.$route.name.includes('CondominiumOverview') || this.$route.name.includes('CondominiumObjectOverview') || (this.$route.name.includes('BuildingDetailsView') && this.$route.query.view === 'condominium')) {
        this.source = 'condoOwners'
        return this.$t('message.onBoarding.interestedParties.modal.condoOwners')
      } else if (this.$route.name.includes('ContactOverview')) {
        this.source = 'contacts'
        return this.$t('message.onBoarding.interestedParties.modal.contacts')
      } else if (this.$route.name.includes('MarketingObjectDetailsView')) {
        this.source = 'requests'
        return this.$t('message.onBoarding.interestedParties.modal.requests')
      } else {
        return this.$t('message.onBoarding.interestedParties.modal.contacts')
      }
    },
    processTenantEntries(element, globalsContacts, joinMap) {
      if (element.id?.startsWith('obj') || element.objectID?.startsWith('obj')) {
        if (element.tenancy?.tenants?.length > 0) {
          const tenants = element.tenancy.tenants.map(x => x.contactId)
          const contacts = this.contactsWithSendableEmail(globalsContacts, tenants)
          tenants.forEach(tenantId => {
            if (contacts.length > 0) {
              joinMap.set(tenantId, contacts.map(x => x.id).filter(x => x !== tenantId))
            }
          })
        }
      }
      if (element.id?.startsWith('tenancy') || element.objectID?.startsWith('tenancy')) {
        if (element.tenants?.length > 0) {
          const tenants = element.tenants.map(x => x.contactId)
          const contacts = this.contactsWithSendableEmail(globalsContacts, tenants)
          tenants.forEach(tenantId => {
            if (contacts.length > 0) {
              joinMap.set(tenantId, contacts.map(x => x.id).filter(x => x !== tenantId))
            }
          })
        }
        if (element.tenant?.contactIds?.length > 0) {
          const tenants = element.tenant.contactIds
          const contacts = this.contactsWithSendableEmail(globalsContacts, tenants)
          tenants.forEach(tenantId => {
            if (contacts.length > 0) {
              joinMap.set(tenantId, contacts.map(x => x.id).filter(x => x !== tenantId))
            }
          })
        }
      }
    },
    processCondoOwnerEntries(element, globalsContacts, joinMap) {
      if (element.id?.startsWith('obj') || element.objectID?.startsWith('obj')) {
        if (element.condominium?.owner?.length > 0) {
          const condoOwners = element.condominium.owner.map(x => x.contactId).concat(element.condominium.roommates?.map(x => x.contactId) || [])
          const contacts = this.contactsWithSendableEmail(globalsContacts, condoOwners)
          condoOwners.forEach(ownerId => {
            if (contacts.length > 0) {
              joinMap.set(ownerId, contacts.map(x => x.id).filter(x => x !== ownerId))
            }
          })
        }
      }
      if (element.id?.startsWith('condominium') || element.objectID?.startsWith('condominium')) {
        if (element.condominiumOwners?.length > 0) {
          const condoOwners = element.condominiumOwners.map(x => x.contactId).concat(element.roommates?.map(x => x.contactId) || [])
          const contacts = this.contactsWithSendableEmail(globalsContacts, condoOwners)
          condoOwners.forEach(ownerId => {
            if (contacts.length > 0) {
              joinMap.set(ownerId, contacts.map(x => x.id).filter(x => x !== ownerId))
            }
          })
        }
        if (element.owner?.contactIds?.length > 0) {
          const condoOwners = element.owner.contactIds.concat(element.owner.roommateContactIds || [])
          const contacts = this.contactsWithSendableEmail(globalsContacts, condoOwners)
          condoOwners.forEach(ownerId => {
            if (contacts.length > 0) {
              joinMap.set(ownerId, contacts.map(x => x.id).filter(x => x !== ownerId))
            }
          })
        }
      }
    },
    processObjectEntries(element, globalsContacts, joinMap) {
      if (element.id?.startsWith('obj') || element.objectID?.startsWith('obj')) {
        if (element.tenants?.tenant?.contactIds?.length > 0) {
          const tenants = element.tenants.tenant.contactIds
          const contacts = this.contactsWithSendableEmail(globalsContacts, tenants)
          tenants.forEach(tenantId => {
            if (contacts.length > 0) {
              joinMap.set(tenantId, contacts.map(x => x.id).filter(x => x !== tenantId))
            }
          })
        }
      }
    },
    processCondominiumEntries(element, globalsContacts, joinMap) {
      if (element.id?.startsWith('obj') || element.objectID?.startsWith('obj')) {
        if (element.condominium?.owner?.contactIds?.length > 0) {
          const condoOwners = element.condominium.owner.contactIds.concat(element.condominium.owner?.roommateContactIds || [])
          const contacts = this.contactsWithSendableEmail(globalsContacts, condoOwners)
          condoOwners.forEach(ownerId => {
            if (contacts.length > 0) {
              joinMap.set(ownerId, contacts.map(x => x.id).filter(x => x !== ownerId))
            }
          })
        }
      }
    },
    hasJoinTenant(id) {
      return this.contactJoinMap.get(id) || []
    },
    loadContacts() {
      this.loading = true
      this.mergeContactsIntoList(this.normalizedAllContacts)
      this.mergeContactsIntoList(this.normalizeContactList(this.contacts))
      if (this.contactIds.length === 0) {
        this.loading = false
        return
      }
      ContactApi.contactResolve(this.contactIds)
        .then(response => {
          const resolved = this.expandCompaniesWithContactPersons(response.persons, response.companies)
          this.mergeContactsIntoList(resolved)
          this.mergeContactsIntoList(this.normalizedAllContacts)
          this.mergeContactsIntoList(this.normalizeContactList(this.contacts))
          this.loading = false
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          this.loading = false
        })
    }
  }
}
</script>
<style lang="scss" scoped>
</style>
